/**
 * Live Preview Panel — typeset mini-resume for the Edit Resume lightbox.
 *
 * Reads the CURRENT in-editor resumejson draft: name + contact line, then every
 * non-omitted section in the current section order using the current custom
 * names. The section that scrollspy says is in view gets a sky wash so the
 * rail, the editor and the paper agree.
 *
 * The page-fit number is an ESTIMATE — bullets are measured with the same
 * PDF line-break model the editor uses (`splitBulletIntoLines`), everything
 * else uses a coarse characters-per-line model.
 *
 * The selector at the top swaps the CONTENT, never the drawing: picking a
 * role-targeted ("dedicated master") resume redraws the SAME schematic with
 * that resume's FROZEN generated content — live structure, frozen wording (see
 * utils/dmPreview). The generic master URL is not a separate option: it is
 * published from this very draft, so it IS the live preview. Nothing here ever
 * triggers a generation, and the real published page is a LINK beside the
 * selector rather than an iframe.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, ExternalLink, X } from 'lucide-react';
import { splitBulletIntoLines } from '../utils/pdfLineBreaks';
import { orderSkillsForIndividual, resolveSkillsSettings } from '../utils/skillsDisplay';
import {
  formatGeneratedAt, getCachedDmContent, mergeDmPreviewResume, setCachedDmContent,
} from '../utils/dmPreview';
import {
  buildPreviewSources, findPreviewSource, previewSourceKey, publishedPageUrl,
  resolvePreviewSource, PREVIEW_SOURCE_LIVE,
} from '../utils/previewSources';
import { api } from '../services/api';
import type {
  DedicatedMaster, DedicatedMasterContent, GenerationType, ResumeJSON, SubcomponentType,
} from '../types';

// Coarse line model for the non-bullet content (chars that fit one printed
// line at each type size) plus how many printed lines fit on a page.
/**
 * Help/Docs anchor root. The panel only exists inside an OPEN resume editor and
 * is revealed by the top bar's Preview toggle, so its keys hang off the
 * editor's path — see `help/highlightTargets.ts` for the catalog entries.
 */
const HELP = 'mainapp.resumes.resumeN.preview';

const CHARS_PER_LINE_BODY = 110;
const CHARS_PER_LINE_LIST = 95;
const LINES_PER_PAGE = 52;
const SECTION_HEADER_LINES = 1.4;

const included = <T extends { include?: string }>(items: T[] | undefined): T[] =>
  (items || []).filter(i => i?.include !== '0');

/**
 * Cascade include filter for experience, mirroring the backend exactly
 * (`subcomponent._pre_filter_include`, also used by
 * `final_files.build_full_resume_user_job`): drop unchecked companies, then
 * unchecked roles inside the survivors, then unchecked bullets — and PRUNE
 * roles left with 0 bullets and companies left with 0 roles, so the preview
 * never shows a company/role header that the rendered resume omits (which would
 * also skew the page-fit line estimate).
 */
const includedExperience = (companies: ResumeJSON['experience']): ResumeJSON['experience'] =>
  included(companies)
    .map(c => ({
      ...c,
      roles: included(c.roles)
        .map(r => ({ ...r, bullets: included(r.bullets) }))
        .filter(r => r.bullets.length > 0),
    }))
    .filter(c => c.roles.length > 0);

function wrapped(text: string, per: number): number {
  const t = (text || '').trim();
  if (!t) return 0;
  return Math.max(1, Math.ceil(t.length / per));
}

interface ResumePreviewPanelProps {
  resume: ResumeJSON;
  /** Reorderable sections in print order (contact renders as the header line). */
  orderedSections: SubcomponentType[];
  getLabel: (component: SubcomponentType) => string;
  genTypes: Record<SubcomponentType, GenerationType>;
  /** Nav key of the block currently in view (scrollspy). */
  activeKey: string | null;
  onClose: () => void;
  /** Account handle — the subdomain every master URL hangs off. */
  username?: string | null;
  /** Resume name; scopes the persisted selection (with the username). */
  resumeName?: string | null;
  /** This resume is the one published at the account's root URL. */
  publishMaster?: boolean;
  /** Role-targeted resumes published beside it. */
  dedicatedMasters?: DedicatedMaster[];
}

export function ResumePreviewPanel({
  resume: liveResume, orderedSections, getLabel, genTypes, activeKey, onClose,
  username, resumeName, publishMaster, dedicatedMasters,
}: ResumePreviewPanelProps) {
  // ---- what the panel is showing -----------------------------------------
  // Only the role-targeted resumes are options: the generic master URL is
  // published from this same draft, so "Live editor preview" already IS it.
  const sources = useMemo(
    () => buildPreviewSources({ dedicatedMasters }),
    [dedicatedMasters],
  );
  const storeKey = previewSourceKey(username, resumeName);
  const [selection, setSelection] = useState<string>(() => {
    let stored: string | null = null;
    try { stored = localStorage.getItem(storeKey); } catch { stored = null; }
    return resolvePreviewSource(stored, sources);
  });
  // A stored path can vanish while the panel is open (a role-targeted resume
  // removed, a path renamed) — fall back rather than preview a ghost.
  const sourceValue = resolvePreviewSource(selection, sources);
  const framed = findPreviewSource(sourceValue, sources);

  const chooseSource = (value: string) => {
    setSelection(value);
    try { localStorage.setItem(storeKey, value); } catch { /* non-fatal */ }
  };

  const openUrl = publishedPageUrl(username, framed, { publishMaster });

  // ---- frozen content for a role-targeted source --------------------------
  // Fetched lazily, ONCE per role-targeted resume (the content only moves when
  // the user regenerates it, which drops the cache from the editor). Never a
  // generation trigger — this is a read of the last one.
  const [dmContent, setDmContent] = useState<DedicatedMasterContent | null>(null);
  const [dmLoading, setDmLoading] = useState(false);
  // Guards against a slow response for a source the user already left.
  const wantedRef = useRef<string>(sourceValue);

  useEffect(() => {
    wantedRef.current = sourceValue;
    if (!framed || !resumeName || !framed.jobid) {
      setDmContent(null); setDmLoading(false);
      return;
    }
    const cached = getCachedDmContent(resumeName, framed.path);
    if (cached !== undefined) {
      setDmContent(cached); setDmLoading(false);
      return;
    }
    let live = true;
    setDmLoading(true); setDmContent(null);
    api.getDedicatedMasterContent(resumeName, framed.jobid)
      .then(res => {
        const content = res?.content || null;
        setCachedDmContent(resumeName, framed.path, content);
        if (!live || wantedRef.current !== sourceValue) return;
        setDmContent(content);
      })
      .catch(() => {
        // Cache the failure as "nothing to show" as well: the list polls while
        // a role-targeted resume generates, and an uncached failure would
        // refetch on every tick. Regenerate (or a reload) clears it.
        setCachedDmContent(resumeName, framed.path, null);
        if (!live || wantedRef.current !== sourceValue) return;
        setDmContent(null);
      })
      .finally(() => { if (live && wantedRef.current === sourceValue) setDmLoading(false); });
    return () => { live = false; };
  }, [sourceValue, framed, resumeName]);

  // Live structure wearing frozen content. Everything below this line draws
  // `resume` and does not care which source it came from.
  const resume: ResumeJSON = useMemo(
    () => (framed && dmContent ? mergeDmPreviewResume(liveResume, dmContent) : liveResume),
    [framed, dmContent, liveResume],
  );
  const generatedAt = framed ? formatGeneratedAt(dmContent?.generated_at) : null;
  const showSchematic = !framed || !!dmContent;

  const visible = orderedSections.filter(s => genTypes[s] !== 'omit');
  const contacts = genTypes.contact === 'omit' ? [] : included(resume.contact);

  // ---- content + line estimate (single pass so they can't disagree) -------
  let lines = 2 + (contacts.length ? 1 : 0);   // name block + contact line

  const blocks: { key: SubcomponentType; label: string; body: React.ReactNode }[] = [];

  for (const component of visible) {
    lines += SECTION_HEADER_LINES;
    let body: React.ReactNode = null;

    if (component === 'summary') {
      const text = (resume.summary || '').trim();
      lines += wrapped(text, CHARS_PER_LINE_BODY);
      body = text
        ? <p className="text-[6.5px] leading-[1.65] text-gray-700">{text}</p>
        : <p className="text-[6.5px] italic text-gray-400">Written per job from your experience.</p>;

    } else if (component === 'highlights') {
      const items = (resume.highlights || []).filter(Boolean);
      if (items.length) {
        items.forEach(h => { lines += wrapped(h, CHARS_PER_LINE_LIST); });
        body = <ul className="space-y-0.5">{items.map((h, i) => <PreviewBullet key={i} text={h} />)}</ul>;
      } else {
        lines += 3;
        body = <p className="text-[6.5px] italic text-gray-400">Selected per job from your strongest matching bullets.</p>;
      }

    } else if (component === 'skills') {
      const skills = included(resume.skills);
      const groups = resume.skill_groups || [];
      if (resolveSkillsSettings(resume.skills_settings).display === 'individual') {
        // "Individual best fit" — one flat comma flow, no group labels, ordered
        // by skill group then resume order (ungrouped last). Per job the
        // backend keeps only the highest-matching skills that fit the line
        // budget; this master view shows them all and says so.
        const names = orderSkillsForIndividual(resume).map(s => s.title).filter(Boolean);
        lines += wrapped(names.join(', '), CHARS_PER_LINE_LIST);
        body = (
          <>
            <p className="text-[6.3px] leading-[1.6] text-gray-700">{names.join(', ')}</p>
            <p className="mt-0.5 text-[6px] italic text-gray-400">Best-fit skills, one flat list — trimmed per job.</p>
          </>
        );
      } else if (groups.length) {
        const rows = groups
          .map(g => ({ title: g.title, names: skills.filter(s => s.group_id === g.id).map(s => s.title) }))
          .filter(r => r.names.length);
        const ungrouped = skills.filter(s => !s.group_id || !groups.some(g => g.id === s.group_id)).map(s => s.title);
        if (ungrouped.length) rows.push({ title: 'Other', names: ungrouped });
        rows.forEach(r => { lines += wrapped(`${r.title}: ${r.names.join(', ')}`, CHARS_PER_LINE_LIST); });
        body = (
          <div className="space-y-0.5">
            {rows.map(r => (
              <p key={r.title} className="text-[6.3px] leading-[1.6] text-gray-700">
                <span className="font-semibold text-primary-700">{r.title}: </span>{r.names.join(', ')}
              </p>
            ))}
          </div>
        );
      } else {
        const names = skills.map(s => s.title);
        lines += wrapped(names.join(', '), CHARS_PER_LINE_LIST);
        body = <p className="text-[6.3px] leading-[1.6] text-gray-700">{names.join(', ')}</p>;
      }

    } else if (component === 'experience') {
      const companies = includedExperience(resume.experience);
      body = (
        <div className="space-y-1.5">
          {companies.map((c, ci) => {
            lines += 1;
            const roles = c.roles;   // already cascade-filtered + pruned above
            return (
              <div key={ci}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[6.8px] font-bold text-gray-800 truncate">{c.company}</span>
                  <span className="text-[6.1px] text-gray-500 whitespace-nowrap">
                    {c.startDate}{(c.current || c.endDate) ? ` – ${c.current ? 'Present' : c.endDate}` : ''}
                  </span>
                </div>
                {roles.map((r, ri) => {
                  lines += 1;
                  const bullets = r.bullets;   // already cascade-filtered above
                  bullets.forEach(b => { lines += splitBulletIntoLines(b.text || '').length; });
                  return (
                    <div key={ri} className="mt-0.5">
                      <div className="text-[6.4px] italic text-gray-600">{r.title}</div>
                      <ul className="mt-0.5 space-y-0.5">
                        {bullets.map((b, bi) => <PreviewBullet key={bi} text={b.text} />)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );

    } else {
      // education / awards / presentations / publications / portfolios
      const items = included((resume as unknown as Record<string, { title?: string; org?: string; issuer?: string; event?: string; year?: string; include?: string }[]>)[component]);
      lines += items.length;
      body = (
        <div className="space-y-0.5">
          {items.map((it, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2">
              <span className="text-[6.4px] text-gray-700 truncate">
                {it.title}
                {(it.org || it.issuer || it.event) && <span className="text-gray-500"> — {it.org || it.issuer || it.event}</span>}
              </span>
              {it.year && <span className="text-[6.1px] text-gray-500 whitespace-nowrap">{it.year}</span>}
            </div>
          ))}
        </div>
      );
    }

    blocks.push({ key: component, label: getLabel(component), body });
  }

  const pages = Math.max(0.1, Math.round((lines / LINES_PER_PAGE) * 10) / 10);
  // Page-fit meter: the track spans whole pages (at least two), a solid segment
  // per full page and a lighter segment for the partly-filled last page.
  const capacity = Math.max(2, Math.ceil(pages));
  const fullPages = Math.floor(pages);
  const partial = Math.max(0, pages - fullPages);
  const fullPct = Math.min(100, (fullPages / capacity) * 100);
  const partPct = Math.min(100 - fullPct, (partial / capacity) * 100);
  const fitNote = partial > 0.04
    ? `page ${fullPages + 1} is ${Math.round(partial * 100)}% full`
    : `${fullPages} full page${fullPages === 1 ? '' : 's'}`;
  const activeLabel = blocks.find(b => b.key === activeKey)?.label
    ?? (activeKey === 'contact' ? getLabel('contact') : null);

  return (
    <div
      className="absolute inset-y-0 right-0 w-[392px] max-w-[62%] bg-white border-l border-gray-200 shadow-[-12px_0_28px_rgba(17,24,39,0.10)] flex flex-col z-20"
      data-help-target={`${HELP}.panel`}
      data-testid="resume-preview-panel"
    >
      <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-gray-200 flex-shrink-0">
        <Eye className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-gray-900 leading-tight">
            {framed ? 'Role-targeted preview' : 'Live preview'}
          </span>
          <span
            className="block text-[11px] text-gray-500 truncate"
            data-testid="preview-subtitle"
            title={framed
              ? `${framed.path} · content from last generation · structure follows this editor`
              : undefined}
          >
            {framed
              ? `${framed.path} · content from last generation · structure follows this editor`
              : 'Untailored base resume · section order applies'}
          </span>
        </span>
        {activeLabel && showSchematic && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200 truncate max-w-[120px] flex-shrink-0">
            {activeLabel}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          data-help-target={`${HELP}.close`}
          className="w-[30px] h-[30px] grid place-items-center flex-shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          aria-label="Hide live preview"
          title="Hide live preview"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Source selector — this draft, or one of its role-targeted resumes. */}
      <div className="flex-shrink-0 px-3.5 py-2 border-b border-gray-200 bg-white">
        <label className="block text-[10.5px] font-semibold uppercase tracking-[0.07em] text-gray-500 mb-1" htmlFor="preview-source">
          Showing
        </label>
        <div className="flex items-center gap-2">
          <select
            id="preview-source"
            value={sourceValue}
            onChange={(e) => chooseSource(e.target.value)}
            data-testid="preview-source-select"
            data-help-target={`${HELP}.source`}
            className="min-w-0 flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-[12.5px] bg-white"
            title="Preview the resume you are editing, or one of its role-targeted resumes"
          >
            <option value={PREVIEW_SOURCE_LIVE}>Live editor preview</option>
            {sources.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {openUrl && (
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-open-page"
              className="inline-flex items-center gap-1 flex-shrink-0 text-[11.5px] text-primary-700 hover:text-primary-800 whitespace-nowrap"
              title="Open the published page in a new tab"
            >
              open published page
              <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>

      {!showSchematic ? (
        <div className="flex-1 overflow-y-auto bg-gray-100 p-3" data-testid="preview-dm-state">
          {dmLoading ? (
            <p className="text-[12px] text-gray-500 text-center py-6" data-testid="preview-dm-loading">
              Loading this resume's generated content…
            </p>
          ) : (
            <div className="text-center py-6">
              <p className="text-[12px] leading-snug text-gray-600" data-testid="preview-dm-empty">
                No generated content yet — regenerate this role-targeted resume to preview it.
              </p>
              {framed?.pending && (
                <p className="mt-2 text-[11px] leading-snug text-amber-700">
                  This one is still being generated.
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto bg-gray-100 p-3">
        <div className="bg-white border border-gray-200 rounded-[3px] shadow-[0_1px_4px_rgba(17,24,39,0.09)] px-5 pt-4 pb-6">
          <div className="text-center">
            <div className="text-[12.5px] font-bold uppercase tracking-[0.09em] text-gray-900">
              {resume.name || 'Your Name'}
            </div>
            {contacts.length > 0 && (
              <div className="mt-1 text-[6.3px] text-gray-500">
                {contacts.map(c => c.title).filter(Boolean).join('  ·  ')}
              </div>
            )}
          </div>
          <div className="mt-2 mb-2 border-t border-gray-300" />

          {blocks.map(b => (
            <div
              key={b.key}
              className={`-mx-1.5 mb-1.5 px-1.5 py-1 rounded-sm border-l-2 transition-colors ${
                activeKey === b.key ? 'bg-primary-50 border-primary-500' : 'border-transparent'
              }`}
            >
              <div className="text-[6.6px] font-bold uppercase tracking-[0.13em] text-primary-700 mb-1">
                {b.label}
              </div>
              {b.body}
            </div>
          ))}

          {blocks.length === 0 && (
            <p className="text-[7px] italic text-gray-400 text-center py-4">Every section is set to Omit.</p>
          )}
        </div>
      </div>
      )}

      {/* Page fit — the same estimate for both sources: a role-targeted preview
          measures its FROZEN content through this editor's structure, which is
          exactly the length question the panel exists to answer. */}
      <div className="flex-shrink-0 border-t border-gray-200 px-3.5 py-2.5 bg-white" data-help-target={`${HELP}.pagefit`}>
        <div className="flex items-center justify-between gap-2 text-[11.5px] text-gray-700">
          <span>Page fit</span>
          <span className="tabular-nums">
            <b className="font-semibold">~{pages} page{pages === 1 ? '' : 's'}</b>
            <span className="text-gray-500"> · {fitNote}</span>
          </span>
        </div>
        <div className="mt-1.5 h-[7px] w-full bg-gray-100 rounded-full overflow-hidden flex gap-[2px]">
          <span className="h-full bg-primary-600" style={{ width: `${fullPct}%` }} />
          <span className="h-full bg-primary-400" style={{ width: `${partPct}%` }} />
        </div>
        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
          {framed
            ? 'Wording is frozen from the last generation; section order, names and omits follow this editor.'
            : 'Estimated at full length. Per job, SkillSnap prunes the lowest-matching items until the resume fits.'}
        </p>
        {generatedAt && (
          <p className="mt-1 text-[11px] leading-snug text-gray-500" data-testid="preview-generated-at">
            Generated {generatedAt}
          </p>
        )}
      </div>
    </div>
  );
}

function PreviewBullet({ text }: { text: string }) {
  return (
    <li className="flex gap-1 text-[6.3px] leading-[1.6] text-gray-700">
      <span className="mt-[3px] w-[2.5px] h-[2.5px] rounded-full bg-gray-400 flex-shrink-0" />
      <span className="min-w-0">{text}</span>
    </li>
  );
}
